import { INTERACTIVE_CARD_CLASS } from '@shared/ui';
import { cn } from '@shared/utils';
import type { DietEntry } from '@diet/types';
import { formatCalories, formatGrams, formatPortion } from '@diet/utils';

export interface EntryRowProps {
  entry: DietEntry;
  onSelect: (entry: DietEntry) => void;
}

/**
 * 單筆紀錄：品項與熱量兩欄，有營養素時在下方直接列出數值。整列可點擊，開啟編輯。
 */
export function EntryRow({ entry, onSelect }: EntryRowProps) {
  const { proteinG, fatG, carbsG } = entry.nutrients;
  /* 只有非預設份量才顯示，見 formatPortion */
  const portionLabel = formatPortion(entry.portion);

  /*
   * 直接列數字，不畫比例條：比例條需要對照總覽卡的圖例才看得懂。
   * 未填的項目整項略過（不顯示 0）：「不知道」與「確實不含」是兩件事。
   */
  const macros = [
    proteinG !== null && `蛋白 ${formatGrams(proteinG)}`,
    fatG !== null && `脂肪 ${formatGrams(fatG)}`,
    carbsG !== null && `碳水 ${formatGrams(carbsG)}`,
  ].filter((item): item is string => item !== false);

  /*
   * 份量與營養素同在第二行，份量在前。
   *
   * 不放第一行熱量旁邊：兩個三位數（200 公克/毫升、290 kcal）緊鄰時容易誤讀成同一個量。
   * 第一行維持「品項 / 熱量」的骨架，第二行才是這一筆的細節。
   *
   * 份量用「公克/毫升」全稱、營養素用縮寫 g，兩種單位因此不會被讀成同一件事。
   */
  const details = portionLabel !== null ? [portionLabel, ...macros] : macros;

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(entry)}
        className={cn(
          INTERACTIVE_CARD_CLASS,
          'flex min-h-[48px] w-full flex-col gap-1 px-3.5 py-3 text-left',
        )}
        aria-label={`編輯 ${entry.name} ${entry.calories} 大卡`}
      >
        <span className="flex w-full items-center gap-3.5">
          <span className="flex-1 truncate text-sm">{entry.name}</span>
          <span className="font-mono text-[0.8125rem] tabular-nums">
            {formatCalories(entry.calories)}
            <small className="ml-0.5 text-[0.625rem] text-muted-foreground">
              kcal
            </small>
          </span>
        </span>

        {/* 第二行只在有細節時出現，沒有的紀錄維持單行，不會看起來像缺了什麼 */}
        {details.length > 0 && (
          <span className="flex w-full">
            <span className="truncate font-mono text-[0.6875rem] tabular-nums text-muted-foreground">
              {details.join(' · ')}
              {/* 單位只在營養素結尾標一次；份量自帶「份」或「公克」 */}
              {macros.length > 0 && <small className="ml-0.5">g</small>}
            </span>
          </span>
        )}
      </button>
    </li>
  );
}
